import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import useApi from '../../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  parseVisitorMappingKey,
  SYSTEM_VISITOR_MAPPING_FIELDS,
  type VisitorFormField,
} from './visitorMappingFields';

type TestRow = {
  sourceKey: string;
  targetKey: string;
  label: string;
  isEmail: boolean;
  options?: string[];
};

type SchedulerTestResponse = {
  provider: 'chili-piper' | 'default' | 'revenue-hero';
  preview:
    | { type: 'url'; url: string }
    | { type: 'default'; submission: Record<string, unknown> }
    | { type: 'revenue-hero'; router_id: string; form_data: Record<string, string> };
};

interface SchedulerIntegrationTestProps {
  integrationId: string;
  integrationName: string;
  fieldMappings: Record<string, string>;
  valueMappings?: Record<string, Record<string, string>>;
  formFields: VisitorFormField[];
  onBack: () => void;
}

export function buildSchedulerTestRows(
  fieldMappings: Record<string, string>,
  formFields: VisitorFormField[],
): TestRow[] {
  return Object.entries(fieldMappings).map(([sourceKey, targetKey]) => {
    const parsed = parseVisitorMappingKey(sourceKey);
    const formField = formFields.find((field) => field.name === parsed.name);
    const systemField = SYSTEM_VISITOR_MAPPING_FIELDS.find(
      (field) =>
        field.key === sourceKey ||
        field.name === parsed.name ||
        field.legacyKey === sourceKey,
    );

    let label = sourceKey;
    if (parsed.source === 'query') {
      label = `Query parameter: ${parsed.name}`;
    } else if (systemField) {
      label = systemField.label;
    } else if (formField) {
      label = formField.label || formField.name;
    }

    const hasOptions =
      formField &&
      (formField.type === 'dropdown' || formField.type === 'multiselect') &&
      Array.isArray(formField.options) &&
      formField.options.length > 0;

    return {
      sourceKey,
      targetKey,
      label,
      isEmail: parsed.name.toLowerCase() === 'email',
      options: hasOptions ? formField.options : undefined,
    };
  });
}

function getErrorMessage(error: any): string {
  const detail = error?.detail ?? error?.response?.data?.detail ?? error?.message;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    return [detail.message, detail.upstream_message, detail.hint].filter(Boolean).join(' ');
  }
  return 'The integration test failed. Check the configuration and try again.';
}

export default function SchedulerIntegrationTest({
  integrationId,
  integrationName,
  fieldMappings,
  valueMappings = {},
  formFields,
  onBack,
}: SchedulerIntegrationTestProps) {
  const { post } = useApi();
  const rows = useMemo(
    () => buildSchedulerTestRows(fieldMappings, formFields),
    [fieldMappings, formFields],
  );
  const [email, setEmail] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SchedulerTestResponse | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitTest = async () => {
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    setError('');
    setResult(null);
    setIsSubmitting(true);
    try {
      const response = await post('/integrations/scheduling/test/', {
        integration_id: integrationId,
        field_mappings: fieldMappings,
        values,
        value_mappings: valueMappings,
        email: trimmedEmail,
      });
      setResult(response.data as SchedulerTestResponse);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setResult(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Edit test data
          </Button>
          <Button variant="outline" size="sm" onClick={submitTest} disabled={isSubmitting}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Run again
          </Button>
        </div>
        <SchedulerCalendarPreview result={result} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-muted-foreground">
            This submits a live test lead. The provider may create or update a CRM record,
            and booking a displayed slot creates a real meeting.
          </p>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        <div className="space-y-2">
          <Label htmlFor="scheduler-test-email">Email</Label>
          <Input
            id="scheduler-test-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="test@example.com"
          />
          <p className="text-xs text-muted-foreground">Required by every scheduler.</p>
        </div>

        {rows.filter((row) => !row.isEmail).map((row) => {
          const selectedValue = values[row.sourceKey] || '';
          const mappedValue = valueMappings[row.sourceKey]?.[selectedValue];
          return (
            <div key={row.sourceKey} className="space-y-2">
              <Label htmlFor={`scheduler-test-${row.sourceKey}`}>{row.label}</Label>
              {row.options ? (
                <Select
                  value={selectedValue}
                  onValueChange={(selected) =>
                    setValues((current) => ({ ...current, [row.sourceKey]: selected }))
                  }
                >
                  <SelectTrigger id={`scheduler-test-${row.sourceKey}`}>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {row.options.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`scheduler-test-${row.sourceKey}`}
                  value={selectedValue}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [row.sourceKey]: event.target.value }))
                  }
                  placeholder={`Value sent to ${row.targetKey}`}
                />
              )}
              <p className="text-xs text-muted-foreground">
                Provider field: <span className="font-mono">{row.targetKey}</span>
                {mappedValue ? (
                  <>
                    {' · '}sends as <span className="font-mono">{mappedValue}</span>
                  </>
                ) : null}
              </p>
            </div>
          );
        })}
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <div className="flex justify-between gap-3 border-t pt-4">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to mappings
        </Button>
        <Button onClick={submitTest} disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isSubmitting ? 'Testing…' : `Test ${integrationName}`}
        </Button>
      </div>
    </div>
  );
}

let defaultSdkPromise: Promise<void> | null = null;
let revenueHeroSdkPromise: Promise<void> | null = null;

function loadSdk(id: string, src: string, globalName: string): Promise<void> {
  if ((window as any)[globalName]) return Promise.resolve();
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  return new Promise((resolve, reject) => {
    const script = existing || document.createElement('script');
    const handleLoad = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    const handleError = () => {
      script.remove();
      reject(new Error(`Failed to load ${globalName}`));
    };
    if (existing?.dataset.loaded === 'true') {
      reject(new Error(`${globalName} loaded without initializing`));
      return;
    }
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!existing) {
      script.id = id;
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

function loadDefaultSdk() {
  defaultSdkPromise ||= loadSdk(
    'default-sdk-script',
    'https://import-cdn.default.com/sdk.js',
    'DefaultSDK',
  ).catch((error) => {
    defaultSdkPromise = null;
    throw error;
  });
  return defaultSdkPromise;
}

function loadRevenueHeroSdk() {
  revenueHeroSdkPromise ||= loadSdk(
    'revenuehero-sdk-script',
    'https://app.revenuehero.io/scheduler.min.js',
    'RevenueHero',
  ).catch((error) => {
    revenueHeroSdkPromise = null;
    throw error;
  });
  return revenueHeroSdkPromise;
}

function isSafeCalendarUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function SchedulerCalendarPreview({ result }: { result: SchedulerTestResponse }) {
  const preview = result.preview;
  const directUrl = preview.type === 'url' ? preview.url : '';
  const [calendarUrl, setCalendarUrl] = useState(directUrl);
  const [error, setError] = useState('');

  useEffect(() => {
    setCalendarUrl(directUrl);
    setError('');
    if (preview.type === 'url') {
      if (!isSafeCalendarUrl(preview.url)) {
        setCalendarUrl('');
        setError('The provider returned an invalid calendar URL.');
      }
      return;
    }

    let cancelled = false;
    let observer: MutationObserver | null = null;
    let timeoutId: number | undefined;

    const initialize = async () => {
      try {
        if (preview.type === 'default') {
          await loadDefaultSdk();
          if (cancelled) return;
          timeoutId = window.setTimeout(() => {
            if (!cancelled) setError('Default did not return a scheduler.');
          }, 10000);
          (window as any).DefaultSDK.submit(preview.submission, {
            autoSchedulerDisplay: false,
            onSuccess: (data: any) => {
              const url = data?.body?.stepDetails?.url;
              if (timeoutId) window.clearTimeout(timeoutId);
              if (!cancelled && typeof url === 'string' && isSafeCalendarUrl(url)) {
                setCalendarUrl(url);
              } else if (!cancelled) {
                setError('Default did not return a valid scheduler URL.');
              }
            },
            onError: (sdkError: Error) => {
              if (timeoutId) window.clearTimeout(timeoutId);
              if (!cancelled) setError(sdkError?.message || 'Default rejected the test submission.');
            },
          });
          return;
        }

        if (preview.type !== 'revenue-hero') return;
        await loadRevenueHeroSdk();
        if (cancelled) return;
        const hero = new (window as any).RevenueHero({ routerId: preview.router_id });
        const sessionData = await hero.submit(preview.form_data);
        if (cancelled) return;

        observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of Array.from(mutation.addedNodes)) {
              if (!(node instanceof HTMLElement)) continue;
              const iframe = node.tagName === 'IFRAME'
                ? (node as HTMLIFrameElement)
                : node.querySelector('iframe');
              if (!iframe?.src || !iframe.src.includes('schedulehero')) continue;
              if (!isSafeCalendarUrl(iframe.src)) {
                setError('RevenueHero returned an invalid scheduler URL.');
                return;
              }

              let root: HTMLElement = node;
              while (root.parentElement && root.parentElement !== document.body) {
                root = root.parentElement;
              }
              observer?.disconnect();
              if (timeoutId) window.clearTimeout(timeoutId);
              root.remove();
              if (!cancelled) setCalendarUrl(iframe.src);
              return;
            }
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        timeoutId = window.setTimeout(() => {
          observer?.disconnect();
          if (!cancelled) setError('RevenueHero did not open a scheduler.');
        }, 7000);
        hero.dialog.open(sessionData);
      } catch (sdkError: any) {
        if (!cancelled) setError(sdkError?.message || 'Failed to load the scheduler.');
      }
    };

    initialize();
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [directUrl, preview]);

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!calendarUrl) {
    return (
      <div className="flex min-h-72 items-center justify-center rounded-xl border">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading calendar…
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-hidden rounded-xl border">
      <iframe
        src={calendarUrl}
        title={`${result.provider} test calendar`}
        width="100%"
        height="600"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        className="block border-0"
      />
    </div>
  );
}
